import { Injectable, signal, inject } from "@angular/core";
import { GateStatusStateService } from "../../../../../services/state/gate-status/gate-status-state.service";
import { DateTime, DateTimeUnit } from "luxon";
import { concatMap, of, forkJoin, map } from "rxjs";
import { ASSET_TYPES } from "../../../../../enums/assetTypes.enum";
import { GateSystem } from "../../../../../models/api/asset/gateSystem";
import { isNotNullOrUndefined } from "../../../../../shared/utils/utils";
import { RealtimeTelemetryMessage } from "../../../../../models/ws/realtimeTelemetry";

import { EnergyToolExtractionService } from "../../../../../services/state/energy-tool-extraction/energy-tool-extraction.service";

import { TelemetryService } from "../../../../../services/api/telemetry/telemetry.service";
import { EnergyToolUpdateService } from "../../../../../services/state/energy-tool-update/energy-tool-update.service";
import { AssetService } from "../../../../../services/api/asset/asset.service";
import { BarChartData, LineChartData } from "../../../../../models/ui/energyToolCharts";
import { GateSystemRef } from "../../../../../models/shared/refs";
import { EnergyToolUtilsService } from "../../../../../services/utils/energy-tool-utils/energy-tool-utils.service";
import { ForecastEnergyLoss } from "../../../../../models/ui/weatherForecast";
import { ForecastTelemetryEntry, TelemetryEntry } from "../../../../../models/shared/telemetry";
import {
  TimeseriesSubscriptionHandle,
  TimeseriesWebsocketService,
} from "../../../../../services/websocket/timeseries-websocket/timeseries-websocket.service";

@Injectable()
export class GateSystemEnergyToolService {
  private readonly gateStatusState = inject(GateStatusStateService);
  public readonly gateStatus = this.gateStatusState.status;
  public readonly gateStatusTimeout = this.gateStatusState.timeout;

  // State signals for Gate System Energy Tool UI:
  private readonly _gatePositionState = signal<{
    value: number;
    timestamp: string;
  } | null>(null);
  readonly gatePositionState = this._gatePositionState.asReadonly();

  private readonly _gatePositionDuration = signal<{
    value: number;
    timestamp: number;
  } | null>(null);
  readonly gatePositionDuration = this._gatePositionDuration.asReadonly();

  private readonly _lastOpenDuration = signal<number | null>(null);
  readonly lastOpenDuration = this._lastOpenDuration.asReadonly();

  private readonly _gateSystemRef = signal<GateSystemRef | null | undefined>(null);
  readonly gateSystemRef = this._gateSystemRef.asReadonly();

  private readonly _longestOpenDuration = signal<number | null>(null);
  readonly longestOpenDuration = this._longestOpenDuration.asReadonly();

  private readonly _energyLoss = signal<number | null>(null);
  readonly energyLoss = this._energyLoss.asReadonly();

  private readonly _energyCost = signal<number | null>(null);
  readonly energyCost = this._energyCost.asReadonly();

  private readonly _expectedEnergyLossPerDay = signal<number | null>(null);
  readonly expectedEnergyLossPerDay = this._expectedEnergyLossPerDay.asReadonly();

  private readonly _insideTemp = signal<TelemetryEntry | null | undefined>(null);
  readonly insideTemp = this._insideTemp.asReadonly();

  private readonly _outsideTemp = signal<TelemetryEntry | null | undefined>(null);
  readonly outsideTemp = this._outsideTemp.asReadonly();

  private readonly _barChartEnergyLoss = signal<BarChartData[] | null>(null);
  readonly barChartEnergyLoss = this._barChartEnergyLoss.asReadonly();

  private readonly _lineChartEnergyLoss = signal<LineChartData[] | null>(null);
  readonly lineChartEnergyLoss = this._lineChartEnergyLoss.asReadonly();

  private readonly _forecastWeather = signal<ForecastTelemetryEntry | null | undefined>(null);
  readonly forecastWeather = this._forecastWeather.asReadonly();

  private readonly _forecastEnergyLoss = signal<ForecastEnergyLoss | null | undefined>(null);
  readonly forecastEnergyLoss = this._forecastEnergyLoss.asReadonly();

  private readonly _energyToolDisabled = signal<boolean>(false);
  readonly energyToolDisabled = this._energyToolDisabled.asReadonly();

  private readonly _cycle = signal<{
    value: number;
    timestamp: string;
  } | null>(null);
  readonly cycle = this._cycle.asReadonly();

  private totalCycles!: number;
  private cycleSelectedTimewindow = "total";
  private telemetryService = inject(TelemetryService);
  private energyExtraction = inject(EnergyToolExtractionService);
  private energyUpdate = inject(EnergyToolUpdateService);
  private energyToolUtils = inject(EnergyToolUtilsService);
  private timeseriesWebsocket = inject(TimeseriesWebsocketService);
  private gateTimeseriesSubscription?: TimeseriesSubscriptionHandle;
  private buildingTimeseriesSubscription?: TimeseriesSubscriptionHandle;

  private assetService = inject(AssetService);

  private resetStates() {
    this._barChartEnergyLoss.set(null);
    this._lineChartEnergyLoss.set(null);
    this._gatePositionState.set(null);
    this._gatePositionDuration.set(null);
    this._lastOpenDuration.set(null);
    this._longestOpenDuration.set(null);
    this._cycle.set(null);
    this._energyLoss.set(null);
    this._insideTemp.set(null);
    this._outsideTemp.set(null);
    this._forecastWeather.set(null);
    this._forecastEnergyLoss.set(null);
    this._expectedEnergyLossPerDay.set(null);
    this._energyCost.set(null);
    this._gateSystemRef.set(null);
  }

  public loadInitialState(gateSystemId: string) {
    this.resetStates();
    this.gateStatusState.load(gateSystemId);

    const now = DateTime.now().toMillis();
    const startOfMonth = DateTime.now().startOf("month").toMillis();

    // Preparing all needed observables:
    const gateSystem$ = this.fetchGateSystem(gateSystemId);
    const telemetry$ = this.fetchGateTelemetryData(gateSystemId);
    const energyLoss$ = this.fetchGateEnergyLoss(gateSystemId, startOfMonth, now);
    const forecast$ = this.telemetryService.getAttributes(
      gateSystemId,
      ASSET_TYPES.GATE_SYSTEM,
      "forecast_weather_data"
    );
    const buildingTemperature$ = gateSystem$.pipe(
      concatMap((gateSystem: GateSystem) => {
        const buildingId = gateSystem?.building?.id;
        if (buildingId)
          return this.telemetryService.getTimeseries(buildingId, ASSET_TYPES.BUILDING, "outside_temperature");
        else return of(null);
      })
    );

    forkJoin({
      gateSystem: gateSystem$,
      telemetry: telemetry$,
      energyLoss: energyLoss$,
      buildingTemperature: buildingTemperature$,
      forecast: forecast$,
    }).subscribe({
      next: (res) => {
        const telemetryResponse = res.telemetry;
        const buildingTemperatureResponse = res.buildingTemperature;
        const energyLossResponse = res.energyLoss;
        const gateSystem = res.gateSystem;
        const forecastData = res.forecast?.forecast_weather_data;

        this._forecastWeather.set(forecastData);

        this._gateSystemRef.set(gateSystem.gateSystem);

        if (isNotNullOrUndefined(gateSystem.core)) {
          this._energyToolDisabled.set(!gateSystem.core);
        }

        if (!gateSystem.core) {
          // cancel extraction if energy tool is disabled
          return;
        }

        // Data for Temperature Widget
        this.energyExtraction.extractAndSetTemp(
          buildingTemperatureResponse,
          "outside_temperature",
          this._outsideTemp.set.bind(this._outsideTemp)
        );

        this.energyExtraction.extractAndSetTemp(
          telemetryResponse,
          "Display_Sensor_Temp",
          this._insideTemp.set.bind(this._insideTemp)
        );

        if (
          isNotNullOrUndefined(telemetryResponse.forecast_energy_loss_today?.[0]?.value) &&
          isNotNullOrUndefined(telemetryResponse.forecast_energy_loss_tomorrow?.[0]?.value) &&
          isNotNullOrUndefined(telemetryResponse.forecast_energy_loss_day_after_tomorrow?.[0]?.value)
        ) {
          this._forecastEnergyLoss.set({
            today: telemetryResponse.forecast_energy_loss_today[0].value,
            tomorrow: telemetryResponse.forecast_energy_loss_tomorrow[0].value,
            dayAfterTomorrow: telemetryResponse.forecast_energy_loss_day_after_tomorrow[0].value,
          });
        }

        // Data for Total Energy Loss Widget
        this.energyExtraction.extractAndSetTotalEnergyLoss(
          energyLossResponse,
          "today",
          this._energyLoss.set.bind(this._energyLoss)
        );

        this.energyExtraction.extractAndSetEnergyCost(
          energyLossResponse,
          "today",
          this._energyCost.set.bind(this._energyCost)
        );
        this.energyExtraction.extractAndSetExpectedEnergyLossPerDay(
          gateSystem,
          this._expectedEnergyLossPerDay.set.bind(this._expectedEnergyLossPerDay)
        );

        // Data for Gate Position Widget
        this.energyExtraction.extractAndSetGateMovementPos(
          telemetryResponse,
          this._gatePositionState.set.bind(this._gatePositionState)
        );

        if (telemetryResponse?.cycle) {
          this.totalCycles = telemetryResponse?.cycle?.[0]?.value;
        }
        // Data for Load Changes Widget
        this.energyExtraction.extractAndSetCycle(telemetryResponse, "total", this._cycle.set.bind(this._cycle));

        // Data for Bar Chart Widget
        this.energyExtraction.extractAndSetBarChartData(
          [
            {
              source: { label: res.gateSystem.label, id: gateSystemId },
              energy_loss: res.energyLoss?.energy_loss,
            },
          ],
          true,
          startOfMonth,
          "month",
          this._barChartEnergyLoss.set.bind(this._barChartEnergyLoss)
        );

        // Data for Line Chart Widget
        this.energyExtraction.extractAndSetLineChartData(
          res.energyLoss?.energy_loss,
          res.energyLoss?.normal_energy_loss,
          startOfMonth,
          "month",
          this._lineChartEnergyLoss.set.bind(this._lineChartEnergyLoss)
        );

        // Data for Open Duration Widget
        this.energyExtraction.extractAndSetOpenDuration(
          telemetryResponse,
          this._gatePositionDuration.set.bind(this._gatePositionDuration),
          this._lastOpenDuration.set.bind(this._lastOpenDuration),
          this._longestOpenDuration.set.bind(this._longestOpenDuration)
        );

        this.initRealtime(gateSystemId, gateSystem.building?.id);
      },
    });
  }

  /**
   * Updates bar chart data for a given time range.
   * @param start - The start of the time range.
   * @param end - The end of the time range.
   * @param timeFrame - The time frame to update bar chart data for.
   * @param gateSystemId - The ID of the Gate System asset.
   * @param assetLabel - The label of the asset to update bar chart data for.
   */
  public updateBarChartForTimeRange(
    start: number,
    end: number,
    timeFrame: DateTimeUnit,
    gateSystemId: string,
    assetLabel: string | undefined
  ) {
    this.energyUpdate.updateBarChartForTimeWindow(
      gateSystemId,
      assetLabel,
      start,
      end,
      timeFrame,
      true,
      true,
      this._barChartEnergyLoss.set.bind(this._barChartEnergyLoss),
      this.fetchGateEnergyLoss.bind(this)
    );
  }

  public updateLineChartForTimeRange(start: number, end: number, timeFrame: DateTimeUnit, gateSystemId: string) {
    this.energyUpdate.updateLineChartForTimeWindow(
      gateSystemId,
      start,
      end,
      timeFrame,
      this._lineChartEnergyLoss.set.bind(this._lineChartEnergyLoss),
      this.fetchGateEnergyLoss.bind(this)
    );
  }

  public updateEnergyLossForTimeWindow(gateSystemId: string, timeWindow: string) {
    this.energyUpdate.updateEnergyLossForTimeWindow(
      gateSystemId,
      timeWindow,
      this._energyLoss.set.bind(this._energyLoss),
      this._energyCost.set.bind(this._energyCost),
      this.fetchGateEnergyLoss.bind(this)
    );
  }

  public updateCycleCountForTimeWindow(gateSystemId: string, timeWindow: string) {
    this.cycleSelectedTimewindow = timeWindow;
    this.energyUpdate.updateCycleForTimeWindow(
      gateSystemId,
      ASSET_TYPES.GATE_SYSTEM,
      timeWindow,
      this._cycle.set.bind(this._cycle)
    );
  }

  private fetchGateSystem(gateSystemId: string) {
    return this.assetService.getAssetDetails(gateSystemId, ASSET_TYPES.GATE_SYSTEM).pipe(
      map((res) => {
        return res as GateSystem;
      })
    );
  }

  private fetchGateTelemetryData(gateSystemId: string) {
    return this.telemetryService.getTimeseries(
      gateSystemId,
      ASSET_TYPES.GATE_SYSTEM,
      "Gate_Movements_Pos,cycle,Display_Sensor_Temp,last_open_duration,longest_open_duration,forecast_weather_data,forecast_energy_loss_today,forecast_energy_loss_tomorrow,forecast_energy_loss_day_after_tomorrow"
    );
  }

  private fetchGateEnergyLoss(gateSystemId: string, start: number, end: number) {
    return this.telemetryService.getTimeseries(
      gateSystemId,
      ASSET_TYPES.GATE_SYSTEM,
      "energy_loss,normal_energy_loss,energy_loss_costs",
      start,
      end
    );
  }

  public destroy() {
    this.resetStates();
    this.gateTimeseriesSubscription?.close();
    this.buildingTimeseriesSubscription?.close();
  }

  private initRealtime(gateSystemId: string, buildingId: string | undefined) {
    const gateDescriptor = {
      entityType: ASSET_TYPES.GATE_SYSTEM,
      entityId: [gateSystemId],
      keys: ["Gate_Movements_Pos", "last_open_duration", "longest_open_duration", "cycle", "Display_Sensor_Temp"],
    };
    if (this.gateTimeseriesSubscription) {
      this.gateTimeseriesSubscription.replace(gateDescriptor);
    } else {
      const subscription = this.timeseriesWebsocket.register(gateDescriptor);
      this.gateTimeseriesSubscription = subscription;
      subscription.messages$.subscribe({
        next: (incomingData) => {
          this.handleRealtimeMessage(incomingData);
        },
        error: () => undefined,
      });
    }

    if (buildingId) {
      const buildingDescriptor = {
        entityType: ASSET_TYPES.BUILDING,
        entityId: [buildingId],
        keys: ["outside_temperature"],
      };
      if (this.buildingTimeseriesSubscription) {
        this.buildingTimeseriesSubscription.replace(buildingDescriptor);
      } else {
        this.buildingTimeseriesSubscription = this.timeseriesWebsocket.register(buildingDescriptor);
      }
    } else {
      this.buildingTimeseriesSubscription?.close();
      this.buildingTimeseriesSubscription = undefined;
    }
  }

  private handleCycleRealtimeChanges(telEntry: TelemetryEntry) {
    const incomingCycleValue = telEntry.value;
    const timestamp = telEntry.timestamp;

    if (this.cycleSelectedTimewindow === "total" || this.cycleSelectedTimewindow === "today") {
      const increment = incomingCycleValue - this.totalCycles;

      this._cycle.update((currentSignalValue: { value: number; timestamp: string } | null) => {
        if (currentSignalValue) {
          this.totalCycles = incomingCycleValue;
          return {
            value: currentSignalValue.value + increment,
            timestamp: this.energyToolUtils.convertTimeStamp(timestamp),
          };
        } else {
          return currentSignalValue;
        }
      });
    } else {
      this.totalCycles = incomingCycleValue;
    }
  }

  private handleRealtimeMessage(message: RealtimeTelemetryMessage) {
    const data = message?.data;

    if (!data) {
      return;
    }
    // Extract and convert all present data points
    const updates: Record<string, TelemetryEntry> = {};

    for (const key in data) {
      const entry = data[key];
      if (entry && entry.value !== undefined) {
        updates[key] = entry;
      }
    }
    Object.entries(updates).forEach(([key, telEntry]) => {
      switch (key) {
        case "cycle":
          this.handleCycleRealtimeChanges(telEntry);
          break;
        case "outside_temperature":
          this.energyExtraction.extractAndSetTemp(
            { outside_temperature: telEntry },
            "outside_temperature",
            this._outsideTemp.set.bind(this._outsideTemp)
          );
          break;
        case "Display_Sensor_Temp":
          this.energyExtraction.extractAndSetTemp(
            { Display_Sensor_Temp: telEntry },
            "Display_Sensor_Temp",
            this._insideTemp.set.bind(this._insideTemp)
          );
          break;
        case "Gate_Movements_Pos":
          this.energyExtraction.extractAndSetGateMovementPos(
            { Gate_Movements_Pos: telEntry },
            this._gatePositionState.set.bind(this._gatePositionState)
          );

          this.energyExtraction.extractAndSetOpenDuration(
            { Gate_Movements_Pos: telEntry },
            this._gatePositionDuration.set.bind(this._gatePositionDuration),
            undefined,
            undefined
          );
          break;
        case "last_open_duration":
          this.energyExtraction.extractAndSetOpenDuration(
            { last_open_duration: telEntry },
            undefined,
            this._lastOpenDuration.set.bind(this._lastOpenDuration),
            undefined
          );
          break;
        case "longest_open_duration":
          this.energyExtraction.extractAndSetOpenDuration(
            { longest_open_duration: telEntry },
            undefined,
            undefined,
            this._longestOpenDuration.set.bind(this._longestOpenDuration)
          );
          break;
        default:
          break;
      }
    });
  }
}
